import { alpha, borderAlpha } from "./components/mobile/primitives";
import { SkillsWorkbenchScreen } from "./WebappSkillsPage.jsx";
import faceSaraChen from "./assets/face-sara-chen.png";
import faceHeartInstitute from "./assets/face-heart-institute.png";
import logoSleepDeepDive from "./assets/logo-sleep-deep-dive.png";

const skillColors = {
  sleep: "#4A90D9",
  nutrition: "#E8853A",
  cardio: "#D9536A",
};

const KIN_GREEN = "#2FB56A";

function BrowserDots() {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {["#FF6B6B", "#F0B800", "#2FB56A"].map((color) => (
        <span
          key={color}
          style={{ width: 8, height: 8, borderRadius: "50%", background: color, opacity: 0.85 }}
        />
      ))}
    </div>
  );
}

function mix(color, percent) {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

function SidebarItem({ label, active = false }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 12,
        background: active ? "rgba(0,0,0,0.05)" : "transparent",
        border: active ? "1px solid rgba(0,0,0,0.06)" : "1px solid transparent",
        color: active ? "var(--color-kin-black)" : "var(--color-kin-muted)",
        fontSize: 11,
        fontWeight: active ? 700 : 600,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </div>
  );
}

function OverviewStat({ label, value, accent }) {
  return (
    <div
      style={{
        borderRadius: 18,
        background: mix(accent, 10),
        border: `1px solid ${mix(accent, 18)}`,
        padding: "14px 16px",
      }}
    >
      <p
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: accent,
        }}
      >
        {label}
      </p>
      <p
        style={{
          marginTop: 8,
          fontSize: 28,
          fontWeight: 700,
          lineHeight: 1,
          color: "var(--color-kin-black)",
          fontFamily: "'Cormorant Garamond', Georgia, serif",
        }}
      >
        {value}
      </p>
    </div>
  );
}

/* ─── Skill reaction card ─────────────────────────────────────────────────── */

function SkillReactionCard({ title, creator, face, logo, category, body, trigger, accent }) {
  return (
    <div
      style={{
        borderRadius: 18,
        overflow: "hidden",
        background: mix(accent, 10),
        border: `1px solid ${mix(accent, 18)}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 170,
      }}
    >
      {/* Color strip */}
      <div style={{ display: "flex", height: 4 }}>
        <div style={{ flex: 3, background: accent }} />
        <div style={{ flex: 1, background: "rgba(255,255,255,0.5)" }} />
        <div style={{ flex: 4, background: mix(accent, 18) }} />
      </div>

      <div
        style={{
          padding: "14px 16px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
        }}
      >
        {/* Skill identity row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {logo ? (
            <img
              src={logo}
              alt=""
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "#fff",
                padding: 3,
                border: `1px solid ${mix(accent, 20)}`,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: mix(accent, 14),
                border: `1px solid ${mix(accent, 24)}`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                color: accent,
                fontFamily: "'Cormorant Garamond', serif",
              }}
            >
              {title.charAt(0)}
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "var(--color-kin-black)",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                }}
              >
                {title}
              </p>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: accent,
                }}
              >
                {category}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
              {face ? (
                <img
                  src={face}
                  alt=""
                  style={{
                    width: 13,
                    height: 13,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `1px solid ${mix(accent, 28)}`,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 13,
                    height: 13,
                    borderRadius: "50%",
                    background: mix(accent, 20),
                    border: `1px solid ${mix(accent, 28)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 7,
                    fontWeight: 700,
                    color: accent,
                  }}
                >
                  {creator.charAt(0)}
                </div>
              )}
              <span style={{ fontSize: 9, fontWeight: 600, color: accent }}>{creator}</span>
            </div>
          </div>
        </div>

        {/* What the skill found */}
        <p style={{ fontSize: 11.5, color: "var(--color-kin-body)", lineHeight: 1.5 }}>{body}</p>

        {/* Data provenance — what triggered this */}
        <div
          style={{
            borderRadius: 10,
            background: "rgba(255,255,255,0.6)",
            border: "1px solid rgba(0,0,0,0.04)",
            padding: "7px 10px",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: KIN_GREEN,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 9, color: "var(--color-kin-muted)", lineHeight: 1.4 }}>
            {trigger}
          </span>
        </div>

        {/* Platform action */}
        <div style={{ marginTop: "auto", fontSize: 11, fontWeight: 700, color: KIN_GREEN }}>
          Open skill →
        </div>
      </div>
    </div>
  );
}

/* ─── Main screen ─────────────────────────────────────────────────────────── */

function HomeWorkbenchScreen() {
  return (
    <article
      style={{
        width: "min(100%, 1180px)",
        borderRadius: 30,
        background:
          "linear-gradient(180deg, color-mix(in srgb, var(--color-kin-white) 88%, transparent) 0%, color-mix(in srgb, var(--color-kin-white) 74%, transparent) 100%)",
        border: "1px solid color-mix(in srgb, var(--color-kin-white-pure) 46%, transparent)",
        boxShadow:
          "0 28px 44px -28px rgba(18,22,19,0.24), 0 10px 18px -12px rgba(18,22,19,0.14), inset 0 1px 0 rgba(255,255,255,0.92)",
        overflow: "hidden",
        backdropFilter: "blur(24px) saturate(135%)",
        position: "relative",
      }}
    >
      {/* Browser chrome */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 18px",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          background: "color-mix(in srgb, var(--color-kin-white) 80%, transparent)",
        }}
      >
        <BrowserDots />
        <div
          style={{
            width: 260,
            borderRadius: 999,
            background: "rgba(0,0,0,0.04)",
            color: "var(--color-kin-muted)",
            fontSize: 10.5,
            fontWeight: 600,
            textAlign: "center",
            padding: "6px 12px",
          }}
        >
          kin.health / home
        </div>
        <div style={{ width: 68 }} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "176px minmax(0, 1fr)",
          gap: 18,
          padding: 18,
        }}
      >
        {/* ── Sidebar ── */}
        <aside
          style={{
            borderRadius: 22,
            background: "color-mix(in srgb, var(--color-kin-sage) 92%, transparent)",
            border: "1px solid rgba(0,0,0,0.05)",
            padding: 14,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ marginBottom: 10 }}>
            <p
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "var(--color-kin-black)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
            >
              Kin
            </p>
          </div>
          <SidebarItem label="Home" active />
          <SidebarItem label="Skills" />
          {/* Expanded skill list */}
          <div
            style={{
              marginLeft: 8,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginTop: -4,
              marginBottom: 4,
            }}
          >
            {[
              { name: "Sleep Deep Dive", color: skillColors.sleep },
              { name: "Metabolic Load", color: skillColors.nutrition },
              { name: "Cardio Baseline", color: skillColors.cardio },
              { name: "Fitness Age", color: "#7B61FF" },
              { name: "Stress Index", color: "#3AADA8" },
              { name: "Recovery Score", color: "#5B8C5A" },
              { name: "Longevity Markers", color: "#9B6B3D" },
            ].map((s) => (
              <div
                key={s.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 8px",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: s.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 10, color: "var(--color-kin-muted)", fontWeight: 500 }}>
                  {s.name}
                </span>
              </div>
            ))}
          </div>
          <SidebarItem label="Health Data" />
          <SidebarItem label="Vault" />
          <SidebarItem label="Briefs" />
          <SidebarItem label="Settings" />

          {/* Profile */}
          <div
            style={{
              marginTop: "auto",
              padding: "12px 12px 13px",
              borderRadius: 16,
              background: "rgba(0,0,0,0.04)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--color-kin-green-soft)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              S
            </div>
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: "var(--color-kin-black)" }}>
                Sverre
              </p>
              <p style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>7 active skills</p>
            </div>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Greeting + Add data button */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 18,
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--color-kin-muted)",
                }}
              >
                Sun, April 13
              </p>
              <h2
                style={{
                  marginTop: 6,
                  fontSize: 40,
                  fontWeight: 700,
                  color: "var(--color-kin-black)",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  lineHeight: 0.98,
                }}
              >
                Good morning, Sverre
              </h2>
            </div>
            {/* + Add data FAB */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 18px 9px 14px",
                borderRadius: 14,
                background: KIN_GREEN,
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                boxShadow: `0 4px 14px -4px ${mix(KIN_GREEN, 50)}`,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1, marginTop: -1 }}>+</span>
              Add data
            </div>
          </div>

          {/* ── Row 1: Featured skill + Data pipeline ── */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 310px", gap: 16 }}>
            {/* Featured skill */}
            <div
              style={{
                borderRadius: 24,
                overflow: "hidden",
                background: `linear-gradient(180deg, ${mix(skillColors.sleep, 18)} 0%, color-mix(in srgb, var(--color-kin-white) 84%, transparent) 72%)`,
                border: `1px solid ${mix(skillColors.sleep, 24)}`,
              }}
            >
              {/* Color strip */}
              <div style={{ display: "flex", height: 4 }}>
                <div style={{ flex: 3, background: skillColors.sleep }} />
                <div style={{ flex: 1, background: "#E8A23C" }} />
                <div style={{ flex: 4, background: mix(skillColors.sleep, 18) }} />
              </div>

              <div style={{ padding: "20px 22px" }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--color-kin-muted)",
                  }}
                >
                  Most relevant today
                </span>

                {/* Skill identity */}
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
                  <img
                    src={logoSleepDeepDive}
                    alt=""
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 11,
                      background: "#fff",
                      padding: 5,
                      border: `1px solid ${mix(skillColors.sleep, 20)}`,
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <h3
                      style={{
                        fontSize: 32,
                        fontWeight: 700,
                        color: "var(--color-kin-black)",
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                        lineHeight: 1,
                      }}
                    >
                      Sleep Deep Dive
                    </h3>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                      <img
                        src={faceSaraChen}
                        alt=""
                        style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }}
                      />
                      <span style={{ fontSize: 10, fontWeight: 600, color: skillColors.sleep }}>
                        Dr. Sara Chen
                      </span>
                      <span style={{ fontSize: 10, color: "var(--color-kin-muted)" }}>
                        · Updated 24m ago
                      </span>
                    </div>
                  </div>
                </div>

                <p
                  style={{
                    marginTop: 14,
                    maxWidth: 540,
                    fontSize: 13,
                    color: "var(--color-kin-body)",
                    lineHeight: 1.7,
                  }}
                >
                  Recovery improved after two earlier dinners and one lower-intensity training
                  block. Deep sleep increased 22% compared to last week.
                </p>

                {/* Key metrics */}
                <div
                  style={{
                    marginTop: 14,
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 10,
                  }}
                >
                  <OverviewStat label="Sleep score" value="84" accent={skillColors.sleep} />
                  <OverviewStat label="Deep sleep" value="1h 42m" accent={skillColors.sleep} />
                  <div
                    style={{
                      borderRadius: 18,
                      padding: "14px 16px",
                      background: alpha("var(--color-kin-lavender)", "12"),
                      border: borderAlpha("var(--color-kin-lavender)", "22"),
                    }}
                  >
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--color-kin-lavender)",
                      }}
                    >
                      Across skills
                    </p>
                    <p
                      style={{
                        marginTop: 6,
                        fontSize: 11,
                        color: "var(--color-kin-body)",
                        lineHeight: 1.45,
                      }}
                    >
                      Nutrition timing may be contributing — meal logs overlap with best recovery
                      nights.
                    </p>
                  </div>
                </div>

                {/* Data provenance */}
                <div
                  style={{
                    marginTop: 12,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.65)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: KIN_GREEN,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 10, color: "var(--color-kin-muted)", lineHeight: 1.4 }}>
                    Based on 14 sleep stage records from Oura export and 2 meal logs — enriched 2h
                    ago
                  </span>
                </div>

                {/* Actions */}
                <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                  <div
                    style={{
                      padding: "9px 18px",
                      borderRadius: 12,
                      background: KIN_GREEN,
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    Open skill
                  </div>
                  <div
                    style={{
                      padding: "9px 18px",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.7)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "var(--color-kin-body)",
                    }}
                  >
                    View history
                  </div>
                </div>
              </div>
            </div>

            {/* ── Feed — right column ── */}
            <div
              style={{
                borderRadius: 22,
                background: "color-mix(in srgb, var(--color-kin-white) 90%, transparent)",
                border: "1px solid rgba(0,0,0,0.05)",
                padding: "16px 16px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 0,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 14,
                }}
              >
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--color-kin-muted)",
                  }}
                >
                  Feed
                </p>
                <span
                  style={{ fontSize: 10, fontWeight: 700, color: "var(--color-kin-green-dark)" }}
                >
                  See all →
                </span>
              </div>

              {/* Feed items */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* ── Item 1: Lab panel (complete) ── */}
                <div
                  style={{
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.05)",
                    background: "rgba(255,255,255,0.6)",
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <p style={{ fontSize: 12, fontWeight: 700, color: "var(--color-kin-black)" }}>
                      Lab panel, March
                    </p>
                    <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>2h ago</span>
                  </div>

                  {/* Health data */}
                  <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {["14 biomarkers", "2 flagged", "1 event"].map((tag, i) => (
                      <span
                        key={tag}
                        style={{
                          padding: "3px 8px",
                          borderRadius: 6,
                          background:
                            i === 1 ? alpha("var(--color-kin-rose)", "10") : "rgba(0,0,0,0.04)",
                          border:
                            i === 1
                              ? borderAlpha("var(--color-kin-rose)", "18")
                              : "1px solid transparent",
                          fontSize: 9,
                          fontWeight: 600,
                          color: i === 1 ? "var(--color-kin-rose)" : "var(--color-kin-muted)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Reactions */}
                  <div style={{ marginTop: 8 }}>
                    <p
                      style={{
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--color-kin-muted)",
                        marginBottom: 4,
                      }}
                    >
                      Reactions
                    </p>
                    {[
                      { name: "Sleep Deep Dive", color: skillColors.sleep, status: "Updated" },
                      { name: "Cardio Baseline", color: skillColors.cardio, status: "Updated" },
                      {
                        name: "Metabolic Load",
                        color: skillColors.nutrition,
                        status: "Needs meal data",
                      },
                    ].map((s) => (
                      <div
                        key={s.name}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "5px 6px",
                          marginLeft: -6,
                          marginRight: -6,
                          borderRadius: 6,
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: s.color,
                            }}
                          />
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: "var(--color-kin-black)",
                            }}
                          >
                            {s.name}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: s.status === "Updated" ? KIN_GREEN : "var(--color-kin-gold)",
                            }}
                          >
                            {s.status}
                          </span>
                          <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Item 2: Tracker entry ── */}
                <div
                  style={{
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.05)",
                    background: "rgba(255,255,255,0.6)",
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <p style={{ fontSize: 12, fontWeight: 700, color: "var(--color-kin-black)" }}>
                      Sleep log
                    </p>
                    <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>6h ago</span>
                  </div>
                  <p style={{ marginTop: 4, fontSize: 10, color: "var(--color-kin-body)" }}>
                    Bedtime 22:40 · Woke 06:50 · Felt rested
                  </p>
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {["1 metric", "1 event"].map((tag) => (
                      <span
                        key={tag}
                        style={{
                          padding: "3px 8px",
                          borderRadius: 6,
                          background: "rgba(0,0,0,0.04)",
                          fontSize: 9,
                          fontWeight: 600,
                          color: "var(--color-kin-muted)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: skillColors.sleep,
                      }}
                    />
                    <span style={{ fontSize: 9, fontWeight: 600, color: "var(--color-kin-black)" }}>
                      Sleep Deep Dive
                    </span>
                    <span
                      style={{ fontSize: 9, fontWeight: 600, color: KIN_GREEN, marginLeft: "auto" }}
                    >
                      Updated
                    </span>
                    <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>→</span>
                  </div>
                </div>

                {/* ── Item 3: Photo upload ── */}
                <div
                  style={{
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.05)",
                    background: "rgba(255,255,255,0.6)",
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <p style={{ fontSize: 12, fontWeight: 700, color: "var(--color-kin-black)" }}>
                      Meal photo
                    </p>
                    <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>Yesterday</span>
                  </div>
                  <p style={{ marginTop: 4, fontSize: 10, color: "var(--color-kin-body)" }}>
                    Dinner — grilled salmon, rice, greens
                  </p>
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <span
                      style={{
                        padding: "3px 8px",
                        borderRadius: 6,
                        background: "rgba(0,0,0,0.04)",
                        fontSize: 9,
                        fontWeight: 600,
                        color: "var(--color-kin-muted)",
                      }}
                    >
                      3 nutrients
                    </span>
                    <span
                      style={{
                        padding: "3px 8px",
                        borderRadius: 6,
                        background: "rgba(0,0,0,0.04)",
                        fontSize: 9,
                        fontWeight: 600,
                        color: "var(--color-kin-muted)",
                      }}
                    >
                      1 meal event
                    </span>
                  </div>
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: skillColors.nutrition,
                      }}
                    />
                    <span style={{ fontSize: 9, fontWeight: 600, color: "var(--color-kin-black)" }}>
                      Metabolic Load
                    </span>
                    <span
                      style={{ fontSize: 9, fontWeight: 600, color: KIN_GREEN, marginLeft: "auto" }}
                    >
                      Updated
                    </span>
                    <span style={{ fontSize: 9, color: "var(--color-kin-muted)" }}>→</span>
                  </div>
                </div>

                {/* ── Item 4: Processing ── */}
                <div
                  style={{
                    borderRadius: 14,
                    border: borderAlpha("var(--color-kin-gold)", "20"),
                    background: alpha("var(--color-kin-gold)", "06"),
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <p style={{ fontSize: 12, fontWeight: 700, color: "var(--color-kin-black)" }}>
                      Apple Health export
                    </p>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "var(--color-kin-gold)",
                      }}
                    >
                      Processing
                    </span>
                  </div>
                  <p style={{ marginTop: 4, fontSize: 10, color: "var(--color-kin-muted)" }}>
                    Extracting sleep stages, heart rate, activity events…
                  </p>
                  <p
                    style={{
                      marginTop: 3,
                      fontSize: 10,
                      color: "var(--color-kin-gold)",
                      fontWeight: 600,
                    }}
                  >
                    8 biomarkers found so far
                  </p>
                  <div
                    style={{
                      marginTop: 8,
                      height: 3,
                      borderRadius: 2,
                      background: "rgba(0,0,0,0.06)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: "62%",
                        height: "100%",
                        borderRadius: 2,
                        background: "var(--color-kin-gold)",
                      }}
                    />
                  </div>
                  <p style={{ marginTop: 6, fontSize: 9, color: "var(--color-kin-muted)" }}>
                    Skills will react when complete
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Row 2: Skill reactions ── */}
          <div
            style={{
              borderRadius: 22,
              background: "color-mix(in srgb, var(--color-kin-white) 88%, transparent)",
              border: "1px solid rgba(0,0,0,0.05)",
              padding: "18px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--color-kin-muted)",
                  }}
                >
                  Also updated
                </p>
                <p
                  style={{
                    marginTop: 4,
                    fontSize: 22,
                    fontWeight: 700,
                    color: "var(--color-kin-black)",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                  }}
                >
                  Reactions
                </p>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-kin-green-dark)" }}>
                All skills →
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 12,
                marginTop: 14,
              }}
            >
              <SkillReactionCard
                title="Metabolic Load"
                creator="NutriLab"
                face={null}
                logo={null}
                category="Nutrition"
                body="Three meal gaps detected. One more logged meal would let me correlate glucose patterns with your new lab biomarkers."
                trigger="Reacted to 14 biomarkers from lab panel"
                accent={skillColors.nutrition}
              />
              <SkillReactionCard
                title="Cardio Baseline"
                creator="Heart Institute"
                face={faceHeartInstitute}
                logo={null}
                category="Cardio"
                body="Resting heart rate stable at 58 bpm. CRP from your lab panel is within range — no cardiovascular inflammation signal."
                trigger="Reacted to CRP and lipid panel from lab results"
                accent={skillColors.cardio}
              />
            </div>
          </div>

          {/* ── Row 3: Health data + Vault ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 16,
            }}
          >
            {/* Health data — biomarkers */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div
                style={{
                  borderRadius: 22,
                  background: "color-mix(in srgb, var(--color-kin-white) 88%, transparent)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  padding: "18px 18px 16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--color-kin-muted)",
                      }}
                    >
                      Health data
                    </p>
                    <p
                      style={{
                        marginTop: 4,
                        fontSize: 22,
                        fontWeight: 700,
                        color: "var(--color-kin-black)",
                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                      }}
                    >
                      Biomarkers
                    </p>
                  </div>
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: "var(--color-kin-green-dark)" }}
                  >
                    Open →
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 10,
                    marginTop: 14,
                  }}
                >
                  <OverviewStat label="Tracked" value="14" accent="var(--color-kin-green-dark)" />
                  <OverviewStat label="Flagged" value="2" accent="var(--color-kin-rose)" />
                </div>

                {/* Flagged biomarkers */}
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    { name: "Vitamin D", value: "22 ng/mL", status: "Below range", flagged: true },
                    { name: "Ferritin", value: "45 ng/mL", status: "Trending up", flagged: false },
                    { name: "HbA1c", value: "5.2%", status: "Normal", flagged: false },
                  ].map((b) => (
                    <div
                      key={b.name}
                      style={{
                        borderRadius: 12,
                        padding: "8px 12px",
                        background: b.flagged
                          ? alpha("var(--color-kin-rose)", "08")
                          : "rgba(0,0,0,0.02)",
                        border: b.flagged
                          ? borderAlpha("var(--color-kin-rose)", "16")
                          : "1px solid transparent",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <span
                          style={{ fontSize: 11, fontWeight: 600, color: "var(--color-kin-black)" }}
                        >
                          {b.name}
                        </span>
                        <span
                          style={{ fontSize: 10, color: "var(--color-kin-muted)", marginLeft: 8 }}
                        >
                          {b.value}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 600,
                          color: b.flagged
                            ? "var(--color-kin-rose)"
                            : "var(--color-kin-green-dark)",
                        }}
                      >
                        {b.status}
                      </span>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: 10, fontSize: 10, color: "var(--color-kin-muted)" }}>
                  Extracted from lab panel · Mar 28
                </p>
              </div>

              {/* Vault summary */}
              <div
                style={{
                  borderRadius: 18,
                  background: "color-mix(in srgb, var(--color-kin-white) 88%, transparent)",
                  border: "1px solid rgba(0,0,0,0.05)",
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "var(--color-kin-muted)",
                    }}
                  >
                    Vault
                  </p>
                  <span
                    style={{ fontSize: 10, fontWeight: 700, color: "var(--color-kin-green-dark)" }}
                  >
                    Open →
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 10,
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  {[
                    { label: "Files", value: "12" },
                    { label: "Sources", value: "3" },
                    { label: "Last upload", value: "2h ago" },
                  ].map((s) => (
                    <div key={s.label} style={{ textAlign: "center" }}>
                      <p
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: "var(--color-kin-black)",
                          fontFamily: "'Cormorant Garamond', Georgia, serif",
                        }}
                      >
                        {s.value}
                      </p>
                      <p style={{ fontSize: 9, color: "var(--color-kin-muted)", marginTop: 2 }}>
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </article>
  );
}

/* ─── Page shell ──────────────────────────────────────────────────────────── */

export default function WebappPage() {
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(ellipse at 15% 10%, rgba(255,208,40,0.06) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, rgba(91,122,102,0.05) 0%, transparent 50%), linear-gradient(168deg, #F8F5EE 0%, #F5F3EC 35%, #F0F2EE 65%, #EEF2EB 100%)",
        fontFamily: "'Manrope', system-ui",
      }}
    >
      <nav
        className="sticky top-0 z-40 flex items-center gap-4 px-6 py-3 sm:px-10"
        style={{
          background: "rgba(247,245,238,0.72)",
          backdropFilter: "blur(20px) saturate(120%)",
          borderBottom: "1px solid rgba(255,255,255,0.64)",
        }}
      >
        <a
          href="/private/design"
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            textDecoration: "none",
          }}
        >
          ← Brandbook
        </a>
        <span style={{ color: "var(--color-kin-olive-light)" }}>·</span>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--color-kin-black)",
          }}
        >
          Webapp Home
        </p>
      </nav>

      <header className="max-w-5xl px-6 pb-10 pt-14 sm:px-10 lg:px-16">
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            marginBottom: 14,
          }}
        >
          Webapp
        </p>
        <h1
          style={{
            fontSize: "clamp(40px, 6vw, 68px)",
            fontWeight: 700,
            color: "var(--color-kin-black)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            lineHeight: 1.0,
            letterSpacing: "-0.01em",
            marginBottom: 20,
          }}
        >
          Home screen
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--color-kin-muted)",
            lineHeight: 1.7,
            maxWidth: 760,
            fontWeight: 400,
          }}
        >
          The desktop starting point. Add data, watch Kin enrich it into structured health records,
          and see your skills react with new insights.
        </p>
      </header>

      <section className="px-6 pb-16 sm:px-10 lg:px-16">
        <HomeWorkbenchScreen />
      </section>

      {/* Skills screen */}
      <header className="max-w-5xl px-6 pb-10 pt-14 sm:px-10 lg:px-16 border-t border-kin-olive/10">
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "var(--color-kin-muted)",
            marginBottom: 14,
          }}
        >
          Webapp
        </p>
        <h2
          style={{
            fontSize: "clamp(40px, 6vw, 68px)",
            fontWeight: 700,
            color: "var(--color-kin-black)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            lineHeight: 1.0,
            letterSpacing: "-0.01em",
            marginBottom: 20,
          }}
        >
          Skills
        </h2>
        <p
          style={{
            fontSize: 17,
            color: "var(--color-kin-muted)",
            lineHeight: 1.7,
            maxWidth: 760,
            fontWeight: 400,
          }}
        >
          Your active skills, their latest analyses, and new skills to discover. Each skill reacts
          to your health data independently.
        </p>
      </header>

      <section className="px-6 pb-16 sm:px-10 lg:px-16">
        <SkillsWorkbenchScreen />
      </section>
    </div>
  );
}
